import { Card, Center, EmptyState, Group, LoadingOverlay, Stack, Table, Tabs } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useHotkeys } from '@mantine/hooks';
import { IconArchive, IconGavel, IconZoom } from '@tabler/icons-react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { FilterButton } from 'components/FilterButton';
import { PageTitle } from 'components/PageTitle';
import dayjs from 'dayjs';
import { usePagination } from 'hooks/use-pagination';
import { i18nKeys, useTranslation } from 'locales';
import { countActiveFilters } from 'modules/Kanban/utils';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { isNonNullish } from 'remeda';
import { handleRowAuxClick, handleRowClick } from 'shared/utils/table';
import { type ThirdPartyCasesFilters, useLoadThirdPartyCases } from '../../api';
import { type ThirdPartyCaseFilter, ThirdPartyCaseFilterDrawer } from '../../components';
import { useBuildThirdPartyCaseColumns } from '../../hooks';

const STORAGE_KEY = 'thirdparty-cases-filters';

type ThirdPartyCaseTab = 'current' | 'closed' | 'all';
type SortableColumn = 'created_at' | 'updated_at';

export const ThirdPartyCasesIndex = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [activeTab, setActiveTab] = useState<ThirdPartyCaseTab>('current');
  const isClosedTab = activeTab === 'closed';
  const isAllTab = activeTab === 'all';

  const [sortBy, setSortBy] = useState<SortableColumn>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { currentPage, resetPagination, pageLimit, PaginationFooter } = usePagination({
    indexPath: 'thirdparty-cases',
  });

  const [isDrawerOpen, { open: openDrawer, close: closeDrawer, toggle: toggleDrawer }] =
    useDisclosure(false);
  useHotkeys([['f', toggleDrawer]]);

  const filterForm = useForm<ThirdPartyCaseFilter>({
    onValuesChange: values => window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values)),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once on mount to restore stored filters
  useEffect(() => {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (isNonNullish(stored)) filterForm.setValues(JSON.parse(stored));
  }, []);

  const subStatus =
    filterForm.values.status === 'not_open' || filterForm.values.status === 'open'
      ? filterForm.values.status
      : undefined;

  const statusParam = isClosedTab
    ? 'closed'
    : isAllTab
      ? filterForm.values.status || undefined
      : (subStatus ?? 'not_closed');

  const queryParams: ThirdPartyCasesFilters = {
    page: currentPage,
    page_limit: pageLimit,
    sort_by: sortBy,
    sort_order: sortOrder,
    status: statusParam,
    ...buildFilterParams(filterForm.values),
  };

  const { thirdPartyCases, isThirdPartyCasesFetching } = useLoadThirdPartyCases(queryParams);

  const handleToggleSort = (column: SortableColumn) => {
    if (sortBy === column) {
      setSortOrder(currentOrder => (currentOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    resetPagination();
  };

  const handleTabChange = (value: string | null) => {
    setActiveTab(value === 'closed' || value === 'all' ? value : 'current');
    filterForm.setFieldValue('status', undefined);
    resetPagination();
  };

  const columns = useBuildThirdPartyCaseColumns({
    sortBy,
    sortOrder,
    onToggleSort: handleToggleSort,
  });

  const table = useReactTable({
    data: thirdPartyCases.data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const activeFiltersCount = countActiveFilters(filterForm.values);
  const noCasesFound = thirdPartyCases.data.length === 0 && !isThirdPartyCasesFetching;
  const hasNoCases = noCasesFound && activeFiltersCount === 0;
  const noFilteredCases = noCasesFound && activeFiltersCount > 0;

  return (
    <>
      <Stack mih="100%" gap={0} pb="xl">
        <PageTitle>
          {t(i18nKeys.THIRD_PARTY_CASES.THIRD_PARTY_CASES_LIST)}
          <PageTitle.Actions>
            <FilterButton
              activeFiltersCount={activeFiltersCount}
              openFilters={openDrawer}
              resetFilters={filterForm.reset}
            />
          </PageTitle.Actions>
        </PageTitle>
        <Tabs value={activeTab} onChange={handleTabChange} mb="md">
          <Tabs.List>
            <Tabs.Tab value="current">{t(i18nKeys.THIRD_PARTY_CASES.CURRENT)}</Tabs.Tab>
            <Tabs.Tab value="closed">{t(i18nKeys.THIRD_PARTY_CASES.CLOSED)}</Tabs.Tab>
            <Tabs.Tab value="all">{t(i18nKeys.THIRD_PARTY_CASES.ALL)}</Tabs.Tab>
          </Tabs.List>
        </Tabs>
        <Card radius={8} p={0}>
          <Group pos="relative" style={{ overflowY: 'auto' }}>
            <LoadingOverlay
              visible={isThirdPartyCasesFetching}
              zIndex={5}
              overlayProps={{ blur: 1 }}
            />
            {!noCasesFound && (
              <Table
                highlightOnHover
                highlightOnHoverColor="purple.0"
                horizontalSpacing="md"
                verticalSpacing="space3"
                stickyHeader
                fz={12}
              >
                <Table.Thead bg="purple.1" style={{ fontSize: 14 }}>
                  {table.getHeaderGroups().map(headerGroup => (
                    <Table.Tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <Table.Th key={header.id} c="purple.5" fw={400}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </Table.Th>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Thead>
                <Table.Tbody>
                  {table.getRowModel().rows.map(row => (
                    <Table.Tr
                      key={row.id}
                      onClick={event =>
                        handleRowClick(event, `/thirdparty-cases/${row.original.id}`, history.push)
                      }
                      onAuxClick={event =>
                        handleRowAuxClick(event, `/thirdparty-cases/${row.original.id}`)
                      }
                      style={{ cursor: 'pointer' }}
                    >
                      {row.getVisibleCells().map(cell => (
                        <Table.Td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {hasNoCases && (
              <Center my="xl" w="100%">
                <EmptyState
                  icon={
                    isClosedTab ? (
                      <IconArchive size={48} stroke={1.5} />
                    ) : (
                      <IconGavel size={48} stroke={1.5} />
                    )
                  }
                  title={t(
                    isClosedTab
                      ? i18nKeys.THIRD_PARTY_CASES.EMPTY_STATE.CLOSED_TITLE
                      : i18nKeys.THIRD_PARTY_CASES.EMPTY_STATE.CURRENT_TITLE,
                  )}
                  description={t(
                    isClosedTab
                      ? i18nKeys.THIRD_PARTY_CASES.EMPTY_STATE.CLOSED_DESCRIPTION
                      : i18nKeys.THIRD_PARTY_CASES.EMPTY_STATE.CURRENT_DESCRIPTION,
                  )}
                />
              </Center>
            )}
            {noFilteredCases && (
              <Center my="xl" w="100%">
                <EmptyState
                  icon={<IconZoom size={48} stroke={1.5} />}
                  title={t(i18nKeys.THIRD_PARTY_CASES.EMPTY_STATE.NO_MATCH_TITLE)}
                  description={t(i18nKeys.THIRD_PARTY_CASES.EMPTY_STATE.NO_MATCH_DESCRIPTION)}
                />
              </Center>
            )}
          </Group>
          <PaginationFooter total={thirdPartyCases.metadata.total} />
        </Card>
      </Stack>
      <ThirdPartyCaseFilterDrawer
        isOpen={isDrawerOpen}
        close={closeDrawer}
        form={filterForm}
        isClosedTab={isClosedTab}
        isAllTab={isAllTab}
      />
    </>
  );
};

function buildFilterParams(values: ThirdPartyCaseFilter): Partial<ThirdPartyCasesFilters> {
  const formatDate = (date?: string | null) =>
    isNonNullish(date) && date !== '' ? dayjs(date).format('YYYY-MM-DD') : undefined;
  const amount = (value?: number | string) =>
    isNonNullish(value) && value !== '' ? String(value) : undefined;

  return {
    filenumber: values.filenumber || undefined,
    debtor_name: values.debtor_name || undefined,
    invoice_reference: values.invoice_reference || undefined,
    case_type: values.case_type || undefined,
    account_manager_id: values.account_manager_id || undefined,
    unread_messages: isNonNullish(values.unread_messages)
      ? values.unread_messages === 'true'
      : undefined,
    total_tvac: amount(values.total_tvac),
    total_tvac_operator: values.total_tvac_operator || undefined,
    remaining_balance: amount(values.remaining_balance),
    remaining_balance_operator: values.remaining_balance_operator || undefined,
    sent_date_after: formatDate(values.sent_date_after),
    sent_date_before: formatDate(values.sent_date_before),
    update_date_after: formatDate(values.update_date_after),
    update_date_before: formatDate(values.update_date_before),
  };
}
